import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit, // Import OnInit for initial check
  signal, // Import signal
  WritableSignal, // Import WritableSignal type
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocalStorageService } from '@libs/shared/services/local-storage.service'; // Adjust path if necessary

@Component({
  selector: 'app-promotion-banner',
  standalone: true,
  imports: [CommonModule], // Keep CommonModule for @if
  template: `
    @if (isVisible$$()) {
      <div
        class="bg-primary-50 dark:bg-primary-900/30 relative flex w-full items-center justify-between gap-4 border-b border-gray-200 p-4 dark:border-gray-700 print:hidden">
        <div class="flex flex-col pl-8">
          <h3
            class="text-primary-800 dark:text-primary-100 text-2xl font-semibold">
            {{ title }}
          </h3>
          <p class="text-primary-700 dark:text-primary-200 text-lg">
            {{ description }}
          </p>
        </div>

        <div class="flex flex-shrink-0 items-center gap-2">
          <ng-content />

          <button
            type="button"
            (click)="dismissBanner()"
            class="text-primary-600 hover:bg-primary-100 hover:text-primary-900 focus:ring-primary-500 dark:text-primary-300 dark:hover:bg-primary-800 ml-2 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg p-1.5 transition duration-75 focus:outline-none focus:ring-2 dark:hover:text-white"
            aria-label="다시 보지 않기">
            <span class="sr-only">배너 닫기 및 다시 보지 않기</span>
            <svg
              class="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    }
  `,
  styles: [
    // Add minor styles if absolutely needed, but prefer Tailwind in template
    `
      :host {
        display: block; /* Ensure the host element takes up block space */
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromotionBannerComponent implements OnInit {
  private readonly localStorageService = inject(LocalStorageService);

  @Input({ required: true }) title = ''; // Default value for safety
  @Input({ required: true }) description = ''; // Default value for safety
  @Input({ required: true }) localStorageKey = ''; // Must be unique per banner

  // Signal to control banner visibility, initialized to true
  isVisible$$: WritableSignal<boolean> = signal(true);

  ngOnInit(): void {
    // Check local storage on component initialization
    this.checkVisibility();
  }

  private checkVisibility(): void {
    if (!this.localStorageKey) {
      console.warn(
        'PromotionBannerComponent: localStorageKey is required for dismiss functionality.'
      );
      // Decide if it should still show if no key provided, perhaps default to true?
      // this.isVisible$$.set(true); // It's already true by default
      return; // Exit if no key is provided
    }

    const storedValue = this.localStorageService.getItem(this.localStorageKey);
    // If the key exists and its value is 'true' (meaning dismissed), hide the banner
    if (storedValue === 'true') {
      this.isVisible$$.set(false);
    } else {
      this.isVisible$$.set(true); // Ensure it's visible otherwise
    }
  }

  /**
   * Dismisses the banner and sets the local storage flag.
   */
  dismissBanner(): void {
    if (!this.localStorageKey) {
      console.error(
        'PromotionBannerComponent: Cannot dismiss banner without a localStorageKey.'
      );
      return;
    }
    this.isVisible$$.set(false); // Hide the banner immediately
    this.localStorageService.setItem(this.localStorageKey, 'true'); // Mark as dismissed
  }
}
